import { getCategories } from 'api/v1/expenses';
import {
  is35To100VATDeductCarCategory,
  isCarDependentIncomeDeductCategory,
} from 'components/pages/Expenses/Form/utils';
import i18n from 'i18n';
import { useTranslation } from 'react-i18next';
import type { UseQueryResult } from 'react-query';
import { useQuery } from 'react-query';
import { ExpensesCacheKeys } from 'types/cacheKeys.types';
import type {
  ExpenseCategoriesDict,
  ExpenseCategory,
} from 'types/expensesCategory.types';
import type { UserCountry, UserLanguage } from 'types/users.types';
import { AvailableCountries } from 'types/users.types';
import { pickerIcons as icons } from 'utils/expenses/categories/icons';
import { round } from 'utils/math';
import useGetCurrentCustomerData from './Authentication/useGetCurrentCustomerData';

const getIcon = (category: any): string => {
  const icon = icons[category.icon as any];
  if (icon) return icon;
  if (category.list && category.list.length > 0)
    return getIcon(category.list[0]);
  return icons.mixed;
};

function modifyEachCategory(
  categories: ExpenseCategory[],
  country: AvailableCountries,
  parentIsQuestion?: boolean,
): ExpenseCategory[] {
  return categories.map((originalCategory) => {
    const modifiedCategory = Object.assign({}, originalCategory, {
      title: parentIsQuestion
        ? originalCategory.title || originalCategory.displayName
        : originalCategory.displayName || originalCategory.title,
      displayName: originalCategory.displayName || originalCategory.title,
      icon: getIcon(originalCategory),
    });
    if (modifiedCategory.list) {
      return Object.assign(modifiedCategory, {
        list: modifyEachCategory(
          modifiedCategory.list.map((child) => ({
            ...child,
            parentTitle: originalCategory.displayName,
          })),
          country,
          !!modifiedCategory.question,
        ),
      });
    }

    if (country === AvailableCountries.belgium) {
      Object.assign(modifiedCategory, {
        additionalData: [
          {
            label: i18n.t('expense.vat_deductibility'),
            value: is35To100VATDeductCarCategory(modifiedCategory.id as string)
              ? '35%-100%'
              : `${round(
                  (modifiedCategory.maxDeductibleVAT as number) * 100,
                )}%`,
          },
          {
            label: i18n.t('expense.item.income_tax_deductibility'),
            value: isCarDependentIncomeDeductCategory(
              modifiedCategory.id as string,
            )
              ? i18n.t('category.taxes.depends_on_the_car')
              : `${round(
                  (1 - (modifiedCategory.dnaPercentage as number)) * 100,
                )}%`,
          },
        ],
      });
    }

    return modifiedCategory;
  });
}

const getCategoriesById = (cates: ExpenseCategory[]) => {
  const map: ExpenseCategoriesDict = {};
  cates.forEach((cate) => {
    if (cate.id) map[cate.id] = cate;
    if (cate.list) Object.assign(map, getCategoriesById(cate.list));
  });
  return map;
};

export type ExpenseCategoriesType = {
  list: ExpenseCategory[];
  dict: ExpenseCategoriesDict;
};

export const useExpensesCategories = (
  customerCountry?: AvailableCountries,
  customerId?: string,
): UseQueryResult<ExpenseCategoriesType, unknown> => {
  const { i18n } = useTranslation();
  const { data: user } = useGetCurrentCustomerData();

  const country = customerCountry || (user?.country as UserCountry);
  const lang = i18n.language as UserLanguage;

  const result = useQuery(
    [ExpensesCacheKeys.CategoriesPicker, country, lang, customerId],
    () =>
      getCategories(country, lang).then((categories) => {
        const list = modifyEachCategory(categories, country);
        return {
          list,
          dict: getCategoriesById(list),
        } as ExpenseCategoriesType;
      }),
    { cacheTime: 0 },
  );

  return result;
};

export default useExpensesCategories;
